import { Capability, UserCapabilities, UserCapabilitiesCache } from './capabilityTypes';
import { Action } from './reducer';

/**
 * How long to wait before flushing a batch of capability requests.
 * All capabilities added during this window are fetched in a single request.
 */
export const BATCH_DELAY_MS = 50;

// Cache keeps track of all requested capabilities
export type UserId = number;
/**
 * The UserCache is used to provide a unique capabilities cache and queued request
 * for each user id passed to `useCapability`.
 */
export class UserCache {
  users: Record<UserId, { capabilitiesCache: CapabilitiesCache; queuedRequest: QueuedRequest }>;

  constructor() {
    this.users = {};
  }

  setCache(id: UserId) {
    this.users[id] = {
      capabilitiesCache: new CapabilitiesCache(),
      queuedRequest: new QueuedRequest(),
    };
  }

  getQueuedRequest(id: UserId | null | undefined): QueuedRequest | null {
    if (id == null) {
      return null;
    }
    if (!this.users[id]) {
      this.setCache(id);
    }
    return this.users[id].queuedRequest;
  }

  getCapabilities(id: UserId | null | undefined): CapabilitiesCache | null {
    if (id == null) {
      return null;
    }
    if (!this.users[id]) {
      this.setCache(id);
    }
    return this.users[id].capabilitiesCache;
  }
}

/**
 * The capabilities cache stores a map of all the requested capabilities and their
 * values. Before the API request for the capability completes, the value is
 * `queued`, indicating that the capability has been queued for request.
 */
export class CapabilitiesCache {
  currentCapabilities: UserCapabilitiesCache;
  constructor() {
    this.currentCapabilities = {};
  }

  update(capabilities: UserCapabilitiesCache) {
    this.currentCapabilities = { ...this.currentCapabilities, ...capabilities };
  }

  remove(toRemove: Capability[]) {
    for (let key of toRemove) {
      delete this.currentCapabilities[key];
    }
  }

  /**
   * Returns all the non-queued capabilities.
   */
  getAll() {
    let caps: UserCapabilities = {};
    for (let [key, val] of Object.entries(this.currentCapabilities)) {
      if (typeof val === 'boolean') (caps as any)[key] = val;
    }
    return caps;
  }
}

type Dispatch = (action: Action) => void;

type Subscriber = {
  dispatch: Dispatch;
  /** The capabilities this subscriber is waiting for. */
  capabilities: Capability[];
};

/**
 * The QueuedRequest batches multiple `useCapability` calls into a single HTTP
 * request and keeps subscribers alive until **their** capabilities resolve.
 *
 * Lifecycle:
 *   1. Capabilities are added via `addCapabilities` and dispatchers via
 *      `addSubscriber(dispatch, caps)`.
 *   2. `scheduleBatch(callback)` starts a one-shot timer. New capabilities
 *      added during the window join the same batch without resetting the timer.
 *   3. When the timer fires the callback should call `flushCapabilities()` to
 *      collect the batch and start the fetch.
 *   4. Subscribers added *after* the timer fires but *before* the fetch
 *      completes are still in the list — they are NOT cleared on flush.
 *   5. On success, `resolveSubscribers(cache)` checks each subscriber: if ALL
 *      of its requested capabilities are resolved (boolean, not `'queued'`) it
 *      dispatches SUCCESS and removes it. Unresolved subscribers remain and
 *      will be checked again when the next batch resolves.
 *   6. On error, `rejectSubscribers(error)` notifies ALL subscribers and
 *      clears the list.
 */
export class QueuedRequest {
  /** Timer for the current batch window. */
  private batchTimeoutId?: ReturnType<typeof setTimeout>;
  /** Capabilities queued for the next fetch. */
  private capabilities: Set<Capability>;
  /** Dispatchers waiting for their specific capabilities to resolve. */
  private subscribers: Subscriber[];

  constructor() {
    this.capabilities = new Set();
    this.subscribers = [];
  }

  addCapabilities(c: Capability[]) {
    c.forEach((c) => this.capabilities.add(c));
  }

  /**
   * Register a subscriber that should be notified when ALL of the given
   * capabilities are resolved in the cache.
   *
   * If the same `dispatch` is already subscribed (e.g. React StrictMode
   * double-invoking effects or a re-render with changed caps), its
   * capability list is replaced so stale caps don't accumulate.
   */
  addSubscriber(dispatch: Dispatch, caps: Capability[]) {
    const existing = this.subscribers.find((s) => s.dispatch === dispatch);
    if (existing) {
      existing.capabilities = [...caps];
    } else {
      this.subscribers.push({ dispatch, capabilities: [...caps] });
    }
  }

  getCapabilities(): Capability[] {
    return Array.from(this.capabilities);
  }

  /**
   * Start the batch timer if not already running. The `onFlush` callback fires
   * once after `BATCH_DELAY_MS`. New capabilities added during the window join
   * the same batch — the timer is NOT reset.
   */
  scheduleBatch(onFlush: () => void) {
    if (this.batchTimeoutId != null) return;
    this.batchTimeoutId = setTimeout(() => {
      this.batchTimeoutId = undefined;
      onFlush();
    }, BATCH_DELAY_MS);
  }

  /**
   * Return and clear the pending capabilities for fetching.
   * Subscribers are NOT cleared — they persist until resolved.
   */
  flushCapabilities(): Capability[] {
    const caps = Array.from(this.capabilities);
    this.capabilities = new Set();
    return caps;
  }

  /**
   * After a successful fetch, check each subscriber: if ALL of its requested
   * capabilities are now resolved (boolean in cache, not `'queued'`), dispatch
   * SUCCESS and remove it. Unresolved subscribers remain for the next batch.
   */
  resolveSubscribers(cache: CapabilitiesCache) {
    const remaining: Subscriber[] = [];
    for (const sub of this.subscribers) {
      // A capability is "resolved" if it is NOT 'queued'.  This covers both
      // boolean values (API returned a value) and undefined/missing (API did
      // not return the capability — the user doesn't have it).  Only
      // 'queued' means a fetch is still in flight for that capability.
      const allResolved = sub.capabilities.every((c) => cache.currentCapabilities[c] !== 'queued');
      if (allResolved) {
        sub.dispatch({ type: 'SUCCESS' });
      } else {
        remaining.push(sub);
      }
    }
    this.subscribers = remaining;
  }

  /**
   * On fetch error, notify only subscribers whose capabilities overlap with
   * the failed batch. Unrelated subscribers (from a later batch) are kept.
   */
  rejectSubscribers(error: Error | null, failedCapabilities: Capability[]) {
    const failed = new Set(failedCapabilities);
    const remaining: Subscriber[] = [];
    for (const sub of this.subscribers) {
      if (sub.capabilities.some((c) => failed.has(c))) {
        sub.dispatch({ type: 'ERROR', error });
      } else {
        remaining.push(sub);
      }
    }
    this.subscribers = remaining;
  }

  clearTimeout() {
    clearTimeout(this.batchTimeoutId);
    this.batchTimeoutId = undefined;
  }

  clear() {
    this.clearTimeout();
    this.capabilities = new Set();
    this.subscribers = [];
  }
}
